/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
/* eslint-disable new-cap */
import AppTheme from '../../theme/AppTheme';
import { CalendarIcon } from '@mui/x-date-pickers';
import CardWrapper from '../../components/wrapper/card';
import { CreateCalendarFormFieldObject } from '../../params/redux/calendar_form_builder';
import { CreateFormFieldObject } from '../../params/redux/form';
import CustomDropdown from '../../components/reusable/dropdown';
import enUS from 'date-fns/locale/en-US';
import ErrorModal from '../../components/modals/error_modal';
import { fetchAllTimeZonesThunk } from '../../redux/thunks/calendar';
import ImageClass from '../../assets';
import LabelWrapper from '../../components/wrapper/label';
import { useParams } from 'react-router';
import { AccessTime, CalendarMonth, CheckCircle } from '@mui/icons-material';
import { AppDispatch, RootState } from '../../redux/store';
import { Avatar, Box, Button, CircularProgress, Paper, SelectChangeEvent, Stack, Typography } from '@mui/material';
import { Calendar as BigCalendar, dateFnsLocalizer } from 'react-big-calendar';
import { BookingAvailabilityHoursObject, CreateBookingParams } from '../../params/redux/booking';
import { brand, red } from '../../theme/themePrimitives';
import { createBookingThunk, fetchBookingDetailThunk, fetchBookingSlotsDetailThunk } from '../../redux/thunks/booking';
import { Form, Formik } from 'formik';
import { format, getDay, parse, startOfWeek } from 'date-fns';
import { generateInitialValues, generateValidationSchema, renderDynamicFields } from '../../helper/booking_form_viewer';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import 'react-big-calendar/lib/css/react-big-calendar.css';

const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales
});

const BookingPage = (props: { disableCustomTheme?: boolean }) => {
    const { eventId } = useParams();
    const dispatch = useDispatch<AppDispatch>();
    const [selectedTimeZone, setSelectedTimeZone] = useState<string>('');
    const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
    const [showThankYou, setShowThankYou] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedTime, setSelectedTime] = useState<BookingAvailabilityHoursObject | null>();
    const { timezoneDetails } = useSelector((state: RootState) => state.calendar);
    const { bookingDetails, availabilityHours, loaders } = useSelector((state: RootState) => state.booking);

    useEffect(() => {
        if (selectedTimeZone === '') {
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
            setSelectedTimeZone(tz);
        }
    }, []);

    const fetchBookingDetails = useCallback(async () => {
        if (!eventId) return;
        setIsLoading(true);
        try {
            await dispatch(fetchBookingDetailThunk(eventId as string)).unwrap();
            await dispatch(fetchAllTimeZonesThunk()).unwrap();
        } catch (e) {
            //
        } finally {
            setIsLoading(false);
        }
    }, [eventId]);

    const handleDateChange = async (date: Date) => {
        try {
            setIsLoading(true);
            setSelectedDate(date);
            await dispatch(fetchBookingSlotsDetailThunk({
                eventId: eventId as string,
                date: format((date as Date), 'yyyy-MM-dd'),
                time_zone: selectedTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone
            })).unwrap();
        } catch (e) {
            //
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchBookingDetails();
        handleDateChange(new Date());
    }, [fetchBookingDetails]);


    const handleTimeZoneChange = async (timezone: string) => {
        try {
            setIsLoading(true);
            setSelectedTimeZone(timezone);
            await dispatch(fetchBookingSlotsDetailThunk({
                eventId: eventId as string,
                date: format((selectedDate as Date), 'yyyy-MM-dd'),
                time_zone: timezone
            })).unwrap();
        } catch (e) {
            //
        } finally {
            setIsLoading(false);
        }
    };

    const onSubmit = async (values: Record<string, unknown>) => {
        try {
            setIsLoading(true);
            const formFields = bookingDetails?.calendar_booking?.form_fields || [];
            const valuesWithLabels: Record<string, unknown> = {};
            Object.entries(values).forEach(([key, value]) => {
                const matchedField = formFields.find((field: CreateFormFieldObject) => field.id === key);
                if (matchedField?.default) {
                    valuesWithLabels.Email = value;
                    return;
                }
                const label = matchedField?.label || key;
                valuesWithLabels[label] = value;
            });

            const data: CreateBookingParams = {
                unique_id: eventId as string,
                timezone: selectedTimeZone,
                name: '',
                email: '',
                set_date: format(selectedDate as Date, 'yyyy-MM-dd'),
                set_time: selectedTime?.start_time as string,
                end_time: selectedTime?.end_time as string,
                duration: bookingDetails?.duration as string,
                form_json: JSON.stringify(valuesWithLabels),
                time_zone: selectedTimeZone
            };
            await dispatch(createBookingThunk(data)).unwrap();
            setShowThankYou(true);
        } catch (e) {
            //
        } finally {
            setIsLoading(false);
        }
    };

    const disabled = loaders.isCreating || isLoading;

    return (
        <AppTheme {...props}>
            {showThankYou
                ? (
                    <Box display="flex" maxWidth="500px" mx="auto" justifyContent="center" pt={2}><Paper
                        elevation={3}
                        sx={{
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            p: 3
                        }}
                    >
                        <Box>
                            <Avatar
                                sx={{
                                    bgcolor: '#e0f0ff',
                                    width: 64,
                                    height: 64,
                                    mx: 'auto',
                                    mb: 2
                                }}
                            >
                                <CheckCircle sx={{ fontSize: 36, color: '#3f9cff' }} />
                            </Avatar>
                            <Typography
                                variant="subtitle1"
                                textAlign="center"
                                sx={{ color: '#3f9cff', fontWeight: 500, mb: 1 }}
                            >
                                Your event has been successfully scheduled
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {bookingDetails?.calendar_booking.event_name}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ color: 'text.secondary', mt: 0.5, mb: 3 }}
                            >
                                Event description – {bookingDetails?.description}
                            </Typography>
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{ mb: 1 }}
                            >
                                <CalendarMonth fontSize="small" color="action" />
                                <Typography variant="body2"> {selectedDate ? format(selectedDate, 'EEE, MMM d, yyyy') : ''}</Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <AccessTime fontSize="small" color="action" />
                                <Typography variant="body2">{selectedTime?.start_time} – {selectedTime?.end_time}</Typography>
                            </Stack>
                        </Box>
                    </Paper>
                    </Box>
                )
                : <Box display="flex" maxWidth="1400px" mx="auto" gap={4} mt={2}>
                    {/* Left Calendar */}
                    <CardWrapper flex={2}>
                        <Typography variant="subtitle1" color="textDisabled" mb={2}>Select the days you are available</Typography>
                        <BigCalendar
                            localizer={localizer}
                            selectable={!disabled}
                            defaultView="month"
                            views={['month']}
                            date={selectedDate || new Date()}
                            onNavigate={(date) => {
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                if (date >= today) {
                                    handleDateChange(date);
                                }
                            }}
                            onSelectSlot={(slotInfo) => {
                                const clickedDate = new Date(slotInfo.start);
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                if (clickedDate >= today) {
                                    handleDateChange(clickedDate);
                                }
                            }}
                            min={new Date()}
                            dayPropGetter={(date) => {
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
                                const isPast = date.getTime() < today.getTime();
                                return {
                                    style: {
                                        backgroundColor: isSelected ? brand[300] : isPast ? '#ebebeb' : 'white',
                                        color: isSelected ? 'white' : isPast ? 'lightgray' : 'black',
                                        pointerEvents: isPast ? 'none' : 'auto',
                                        cursor: 'pointer',
                                        opacity: isPast ? 0.6 : 1
                                    }
                                };
                            }}
                            style={{ height: 500 }}
                        />

                        <Box mt={5}>
                            <LabelWrapper label="Select Timezone" />
                            <Box width="max-content">
                                <CustomDropdown
                                    value={selectedTimeZone}
                                    options={timezoneDetails.allTimezones.map((item: { zone: string; time: string }) => ({
                                        label: `${item.zone} (${item.time})`,
                                        value: item.zone
                                    }))}
                                    onChange={(e: SelectChangeEvent<unknown>) => handleTimeZoneChange(e.target.value as string)}
                                    MenuProps={{
                                        PaperProps: {
                                            style: {
                                                maxHeight: 500,
                                                overflowY: 'auto'
                                            }
                                        }
                                    }}
                                    disabled={disabled}
                                    sx={{ mb: 0 }}
                                />
                            </Box>
                        </Box>
                    </CardWrapper>

                    {/* Right Panel */}
                    <Box flex={1}>
                        <CardWrapper>
                            {isLoading
                                ? (<Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                                    <CircularProgress />
                                </Box>
                                )
                                : <Box display="flex" flexDirection="column" gap={2}>
                                    <img
                                        src={ImageClass.logoLight}
                                        alt="No slots opened"
                                        width="40%"
                                        height="auto"
                                    />
                                    {availabilityHours?.length === 0
                                        ? <Box
                                            display="flex"
                                            justifyContent="center"
                                            flexDirection="column"
                                            bgcolor={red[50]}
                                            color={red[300]}
                                            p={2}
                                            borderRadius={1}
                                        >
                                            <Typography variant="subtitle2" fontWeight="bold">
                                                No slots are opened on this day.
                                            </Typography>
                                        </Box>
                                        : <Box display="flex" gap={1} alignItems="center" color="text.secondary">
                                            <CalendarIcon />
                                            <Typography variant="subtitle1" color="textSecondary" fontWeight="bold">
                                                Book an appointment here
                                            </Typography></Box>}
                                    <CardWrapper>
                                        <Box display="flex" flexDirection="column" gap={1}>
                                            <Typography variant="subtitle1" color="textSecondary" fontWeight="bold">Event Name - {bookingDetails?.event_name}</Typography>
                                            <Typography variant="subtitle2" color="textSecondary" fontWeight="bold">{bookingDetails?.description}</Typography>
                                            <Typography variant="subtitle2" color="textSecondary">Host - {bookingDetails?.host}</Typography>
                                            <Box display="flex" alignItems="center">
                                                <img src={ImageClass.googleMeetLogo} alt="Logo" width="45" height="30" />
                                                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: brand[300] }}>Google Meet</Typography>
                                            </Box>
                                        </Box>
                                    </CardWrapper>
                                    {selectedDate && availabilityHours?.length > 0 && <Box>
                                        <Typography variant="subtitle1" fontSize="16px" color="textSecondary" fontWeight="bold">
                                            Select a start time for the {bookingDetails?.duration} minute meeting
                                        </Typography>
                                        <Box display="flex" flexWrap="wrap" gap={2} mt={1}>
                                            {availabilityHours?.map((item, index) => <Box key={index}>
                                                <Button
                                                    variant="outlined"
                                                    onClick={() => setSelectedTime(item)}
                                                    disabled={item.booking_status === 'disabled'}
                                                    color={item.start_time === selectedTime?.start_time ? 'primary' : 'secondary'}
                                                >
                                                    {item.start_time}
                                                </Button>
                                            </Box>)}
                                        </Box>
                                    </Box>}
                                    {selectedDate && availabilityHours?.length > 0 && selectedTime && (
                                        <Formik
                                            initialValues={generateInitialValues((bookingDetails?.calendar_booking?.form_fields || []).map((field) => ({
                                                ...field,
                                                min: field.min ?? null,
                                                max: field.max ?? null
                                            })) as unknown as CreateCalendarFormFieldObject[])}
                                            validationSchema={generateValidationSchema((bookingDetails?.calendar_booking?.form_fields || []).map((field) => ({
                                                ...field,
                                                min: field.min ?? null,
                                                max: field.max ?? null
                                            })) as unknown as CreateCalendarFormFieldObject[])}
                                            onSubmit={onSubmit}
                                        >
                                            {(formikProps) => (
                                                <Form>
                                                    <Box display="flex" flexDirection="column" gap={2}>
                                                        {renderDynamicFields((bookingDetails?.calendar_booking?.form_fields || []).map((field) => ({
                                                            ...field,
                                                            min: field.min ?? null,
                                                            max: field.max ?? null
                                                        })) as unknown as CreateCalendarFormFieldObject[], formikProps)}
                                                        <Button variant="contained" type="submit" disabled={disabled}>Confirm Event</Button>
                                                    </Box>
                                                </Form>
                                            )}
                                        </Formik>
                                    )}
                                </Box>}
                        </CardWrapper>
                    </Box>
                </Box>}
                <ErrorModal />
        </AppTheme>
    );
};

export default BookingPage;
